import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  Package, 
  MessageSquare, 
  FileText, 
  Mail, 
  LogOut,
  Plus,
  Trash2,
  Edit,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_press-studio/artifacts/4au2b8ob_Logo-02.png";

// Sidebar Component
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Package, label: "Products", path: "/admin/dashboard/products" },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/dashboard/inquiries" },
    { icon: FileText, label: "Quote Requests", path: "/admin/dashboard/quotes" },
    { icon: Mail, label: "Contact Messages", path: "/admin/dashboard/messages" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("velvetpress_admin");
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  return (
    <aside className="w-64 min-h-screen bg-[#141414] border-r border-white/10 fixed left-0 top-0" data-testid="admin-sidebar">
      <div className="p-6">
        <img src={LOGO_URL} alt="Velvet Press" className="h-12 w-auto" />
      </div>
      <nav className="px-4 space-y-1">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${
              location.pathname === item.path
                ? "bg-[#D4AF37]/20 text-[#D4AF37]"
                : "text-gray-400 hover:bg-white/5 hover:text-white"
            }`}
            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon size={20} />
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 px-4 py-3 w-full text-gray-400 hover:text-red-400 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({
    products: 0,
    inquiries: 0,
    quotes: 0,
    messages: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, inquiries, quotes, messages] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/inquiries`),
          axios.get(`${API}/quotes`),
          axios.get(`${API}/contact`),
        ]);
        setStats({
          products: products.data.length,
          inquiries: inquiries.data.length,
          quotes: quotes.data.length,
          messages: messages.data.length,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: "Total Products", value: stats.products, color: "text-[#D4AF37]" },
    { label: "Product Inquiries", value: stats.inquiries, color: "text-blue-400" },
    { label: "Quote Requests", value: stats.quotes, color: "text-green-400" },
    { label: "Contact Messages", value: stats.messages, color: "text-purple-400" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <h1 className="font-outfit text-2xl text-white mb-8">Dashboard Overview</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-[#141414] p-6 rounded-sm border border-white/10"
          >
            <p className="text-gray-400 text-sm mb-2">{stat.label}</p>
            <p className={`font-outfit text-3xl ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Products Management
const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    category_id: "",
    description: "",
    image: "",
  });

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success("Product created successfully");
      }
      setIsModalOpen(false);
      setEditingProduct(null);
      setFormData({ name: "", slug: "", category_id: "", description: "", image: "" });
      fetchData();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      category_id: product.category_id,
      description: product.description || "",
      image: product.image || "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Product deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  return (
    <div data-testid="products-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-outfit text-2xl text-white">Products Management</h1>
        <Button
          onClick={() => {
            setEditingProduct(null);
            setFormData({ name: "", slug: "", category_id: "", description: "", image: "" });
            setIsModalOpen(true);
          }}
          className="btn-gold"
          data-testid="add-product-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Product
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="bg-[#141414] rounded-sm border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left text-gray-400 px-6 py-4 font-medium">Product</th>
                <th className="text-left text-gray-400 px-6 py-4 font-medium">Category</th>
                <th className="text-right text-gray-400 px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-t border-white/10">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                      <img
                        src={product.image || "https://via.placeholder.com/50"}
                        alt={product.name}
                        className="w-12 h-12 rounded-sm object-cover"
                      />
                      <span className="text-white">{product.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-400">{product.category_name}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-gray-400 hover:text-[#D4AF37] p-2"
                      data-testid={`edit-${product.slug}`}
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-gray-400 hover:text-red-400 p-2"
                      data-testid={`delete-${product.slug}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Product Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: generateSlug(e.target.value),
                  });
                }}
                className="input-dark"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="input-dark" data-testid="product-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="text-gray-300">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Image URL</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="input-dark"
                placeholder="https://..."
                data-testid="product-image-input"
              />
            </div>
            <div>
              <Label className="text-gray-300 mb-2 block">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-dark"
                data-testid="product-description-input"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn-outline-gold"
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-gold" data-testid="save-product-btn">
                {editingProduct ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Inquiries List
const InquiriesList = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const response = await axios.get(`${API}/inquiries`);
        setInquiries(response.data);
      } catch (error) {
        console.error("Error fetching inquiries:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInquiries();
  }, []);

  const getStatusClass = (status) => {
    switch (status) {
      case "new": return "status-new";
      case "contacted": return "status-contacted";
      case "completed": return "status-completed";
      default: return "status-new";
    }
  };

  return (
    <div data-testid="inquiries-list">
      <h1 className="font-outfit text-2xl text-white mb-8">Product Inquiries</h1>
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : inquiries.length === 0 ? (
        <div className="bg-[#141414] p-8 rounded-sm border border-white/10 text-center">
          <p className="text-gray-400">No inquiries yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <div key={inquiry.id} className="bg-[#141414] p-6 rounded-sm border border-white/10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white font-medium">{inquiry.product_name}</h3>
                  <p className="text-gray-400 text-sm">
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs ${getStatusClass(inquiry.status)}`}>
                  {inquiry.status}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Name</p>
                  <p className="text-gray-300">{inquiry.name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-300">{inquiry.phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="text-gray-300">{inquiry.email}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Quote Requests List
const QuoteRequestsList = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await axios.get(`${API}/quotes`);
        setQuotes(response.data);
      } catch (error) {
        console.error("Error fetching quotes:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, []);

  const getStatusClass = (status) => {
    switch (status) {
      case "new": return "status-new";
      case "contacted": return "status-contacted";
      case "completed": return "status-completed";
      default: return "status-new";
    }
  };

  return (
    <div data-testid="quotes-list">
      <h1 className="font-outfit text-2xl text-white mb-8">Quote Requests</h1>
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : quotes.length === 0 ? (
        <div className="bg-[#141414] p-8 rounded-sm border border-white/10 text-center">
          <p className="text-gray-400">No quote requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-[#141414] p-6 rounded-sm border border-white/10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white font-medium">{quote.name}</h3>
                  <p className="text-gray-400 text-sm">
                    {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs ${getStatusClass(quote.status)}`}>
                  {quote.status}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="text-gray-300">{quote.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-300">{quote.phone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Product Type</p>
                  <p className="text-gray-300">{quote.product_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Quantity</p>
                  <p className="text-gray-300">{quote.quantity}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Description</p>
                <p className="text-gray-300 text-sm mt-1">{quote.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Contact Messages List
const ContactMessagesList = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${API}/contact`);
        setMessages(response.data);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  return (
    <div data-testid="messages-list">
      <h1 className="font-outfit text-2xl text-white mb-8">Contact Messages</h1>
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="bg-[#141414] p-8 rounded-sm border border-white/10 text-center">
          <p className="text-gray-400">No messages yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message.id} className="bg-[#141414] p-6 rounded-sm border border-white/10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white font-medium">{message.subject}</h3>
                  <p className="text-gray-400 text-sm">
                    From: {message.name} • {new Date(message.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="text-gray-300">{message.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-300">{message.phone}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Message</p>
                <p className="text-gray-300 text-sm mt-1">{message.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("velvetpress_admin");
    if (!isLoggedIn) {
      navigate("/admin");
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <Sidebar />
      <div className="ml-64 p-8">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="products" element={<ProductsManagement />} />
          <Route path="inquiries" element={<InquiriesList />} />
          <Route path="quotes" element={<QuoteRequestsList />} />
          <Route path="messages" element={<ContactMessagesList />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;
